import { useState, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, FileText, ChevronDown, ChevronRight, Search, ClipboardList, Plus } from 'lucide-react';
import { LoadingState } from '@systemdox/ui';
import { PlanningStep } from '@/components/agent/PlanningStep';
import { PlanHistoryList } from '@/components/agent/PlanHistoryList';
import { planHistoryStore, type PlanHistoryEntry } from '@/services/planHistoryStore';
import { planningApi, type PlanningProject } from '@/services/planningApi';
import type { GeneratedPlan } from '@/services/planningApi';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { PageEmptyState } from '@/components/onboarding/PageEmptyState';
import { cn } from '@/lib/utils';

type PlanView = 'create' | 'drafts' | 'github';
type PlanBucket = 'inProgress' | 'backlog' | 'done';

/** Bucket a plan by its overall progress (from item statusCounts, #573).
 *  Done: closed, or every counted item done. In Progress: some work started.
 *  Backlog: nothing started (all todo, or no items). */
function planBucket(p: PlanningProject): PlanBucket {
  const c = p.statusCounts;
  const counted = c ? c.todo + c.inProgress + c.done : 0;
  if (p.closed || (c && counted > 0 && c.done === counted)) return 'done';
  if (c && (c.inProgress > 0 || c.done > 0)) return 'inProgress';
  return 'backlog';
}

function PlanCard({ project }: { project: PlanningProject }) {
  const counts = project.statusCounts;
  const counted = counts ? counts.todo + counts.inProgress + counts.done : 0;
  return (
    <a
      href={project.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-blue-300 dark:hover:border-blue-700 transition-colors group"
    >
      <div className="flex items-center gap-3">
        <FileText className="w-4 h-4 text-slate-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">
            {project.title}
          </p>
          {project.description && (
            <p className="text-xs text-slate-400 truncate">{project.description}</p>
          )}
        </div>
        <span className="text-xs text-slate-400 shrink-0">
          {project.itemCount} items
        </span>
        <span className={cn(
          'text-[10px] px-1.5 py-0.5 rounded shrink-0',
          project.closed
            ? 'bg-slate-100 dark:bg-slate-800 text-slate-500'
            : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
        )}>
          {project.closed ? 'Closed' : 'Open'}
        </span>
        <ExternalLink className="w-3.5 h-3.5 text-slate-300 group-hover:text-blue-500 shrink-0" />
      </div>

      {/* Per-plan progress: Done / In Progress / Todo */}
      {counts && counted > 0 && (
        <div className="mt-2.5 pl-7">
          <div className="flex h-1.5 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-800">
            {counts.done > 0 && (
              <div className="bg-emerald-500" style={{ width: `${(counts.done / counted) * 100}%` }} />
            )}
            {counts.inProgress > 0 && (
              <div className="bg-blue-500" style={{ width: `${(counts.inProgress / counted) * 100}%` }} />
            )}
          </div>
          <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-500 dark:text-slate-400">
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              {counts.done} Done
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-500" />
              {counts.inProgress} In Progress
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-600" />
              {counts.todo} Todo
            </span>
            <span className="ml-auto font-medium text-slate-600 dark:text-slate-300">
              {Math.round((counts.done / counted) * 100)}%
            </span>
          </div>
        </div>
      )}
    </a>
  );
}

function CollapsibleSection({
  title, count, defaultOpen, children,
}: { title: string; count: number; defaultOpen: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="mb-3">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-1 py-1.5 text-left"
      >
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {title}
        </span>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500">
          {count}
        </span>
      </button>
      {open && (
        count === 0 ? (
          <p className="pl-6 py-1 text-xs text-slate-400">Nothing here.</p>
        ) : (
          <div className="space-y-2 mt-1">{children}</div>
        )
      )}
    </section>
  );
}

export default function PlanPage() {
  const navigate = useNavigate();
  const { selectedOrg } = useWorkspace();
  const [view, setView] = useState<PlanView>('github');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [restoredEntry, setRestoredEntry] = useState<PlanHistoryEntry | null>(null);

  // GitHub projects
  const [projects, setProjects] = useState<PlanningProject[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [search, setSearch] = useState('');

  const handleBoardPopulated = () => {
    // Execution happens in the user's own AI tool via MCP — land on the
    // Connect page, which guides the connection.
    navigate('/connect');
  };

  // Reset when the user switches org
  useEffect(() => {
    setRestoredEntry(null);
    setRefreshTrigger((n) => n + 1);
  }, [selectedOrg]);

  // Load GitHub projects when switching to that view
  useEffect(() => {
    if (view === 'github') {
      setProjectsLoading(true);
      planningApi.listProjects()
        .then(setProjects)
        .catch(() => setProjects([]))
        .finally(() => setProjectsLoading(false));
    }
  }, [view]);

  // Client-side filter over the plans list (title + description)
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(
      (p) => p.title.toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q),
    );
  }, [projects, search]);

  // Group plans into Work in Progress / Backlog / Done (#573 statusCounts)
  const grouped = useMemo(() => {
    const buckets: Record<PlanBucket, PlanningProject[]> = { inProgress: [], backlog: [], done: [] };
    for (const p of filtered) buckets[planBucket(p)].push(p);
    return buckets;
  }, [filtered]);

  const handlePlanGenerated = useCallback((description: string, plan: GeneratedPlan) => {
    const orgKey = selectedOrg || 'default';
    const entry = planHistoryStore.save(orgKey, description, plan);
    setRestoredEntry(entry);
    setRefreshTrigger((n) => n + 1);
  }, [selectedOrg]);

  const handleRestorePlan = useCallback((entry: PlanHistoryEntry) => {
    window.location.hash = '';
    setRestoredEntry(entry);
    setView('create');
  }, []);

  // The plans list is the default page; Create and Drafts are secondary
  // views reached from the top-right actions.
  const ACTIONS: { key: PlanView; label: string }[] = [
    { key: 'create', label: 'Create' },
    { key: 'drafts', label: 'Drafts' },
  ];

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Toolbar: search over plans (or back-to-plans), actions top right */}
      <div className="flex items-center gap-3 px-4 py-3 shrink-0">
        {view === 'github' ? (
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search plans..."
              aria-label="Search plans"
              className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
            />
          </div>
        ) : (
          <button
            onClick={() => setView('github')}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Plans
          </button>
        )}
        <div className="ml-auto inline-flex rounded-lg bg-slate-100 dark:bg-slate-800 p-0.5 shrink-0">
          {ACTIONS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setView(key)}
              className={cn(
                'px-4 py-1.5 text-xs font-medium rounded-md transition-colors',
                view === key
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* View content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {view === 'create' && (
          <PlanningStep
            onBoardPopulated={handleBoardPopulated}
            onPlanGenerated={handlePlanGenerated}
            restoredEntry={restoredEntry}
          />
        )}

        {view === 'drafts' && (
          <div className="flex-1 overflow-y-auto p-4 w-full">
            <PlanHistoryList
              org={selectedOrg || 'default'}
              activePlanId={restoredEntry?.id}
              onSelect={handleRestorePlan}
              refreshTrigger={refreshTrigger}
              onCreate={() => setView('create')}
            />
          </div>
        )}

        {view === 'github' && (
          <div className="flex-1 overflow-y-auto p-4 w-full">
            {projectsLoading ? (
              <LoadingState size="lg" label="Loading projects…" />
            ) : projects.length === 0 ? (
              <PageEmptyState
                icon={ClipboardList}
                title="There are no plans yet"
                description="A plan turns an idea into epics and issues on a GitHub project board. Describe what you want to build and SystemDox drafts the plan for you to review — nothing is created until you populate the board."
              >
                <button
                  onClick={() => setView('create')}
                  className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-violet-700"
                >
                  <Plus className="h-4 w-4" />
                  Create a Plan
                </button>
              </PageEmptyState>
            ) : filtered.length === 0 ? (
              <p className="py-12 text-center text-sm text-slate-400">
                No plans match &ldquo;{search}&rdquo;.
              </p>
            ) : (
              <>
                <CollapsibleSection title="Work in Progress" count={grouped.inProgress.length} defaultOpen>
                  {grouped.inProgress.map((p) => <PlanCard key={p.number} project={p} />)}
                </CollapsibleSection>
                <CollapsibleSection title="Work Backlog" count={grouped.backlog.length} defaultOpen>
                  {grouped.backlog.map((p) => <PlanCard key={p.number} project={p} />)}
                </CollapsibleSection>
                {/* Work Done collapsed by default — completed work is least
                    relevant day to day. */}
                <CollapsibleSection title="Work Done" count={grouped.done.length} defaultOpen={false}>
                  {grouped.done.map((p) => <PlanCard key={p.number} project={p} />)}
                </CollapsibleSection>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
